import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useTranslation } from 'react-i18next';

import InfoIconPrimary from '../../../../../../../../assets/icons/InfoIconPrimary';
import EmptyScreen from '../../../../../../../../components/empty-screen/EmptyScreen';
import TooltipIcon from '../../../../../../../../components/tooltip-icon/TooltipIcon';
import { IPermission, PermissionStatusType } from '../../../../../../../../hooks/api/permissions';
import { Locale } from '../../../../../../../../i18n';
import { genericSortFunction } from '../../../../../../../../shared/helpers/column-sort-functions';
import { parseDateWithoutTime } from '../../../../../../../../shared/helpers/parse-date';
import AddCompanyAccessButton from '../add-company-access-button/AddCompanyAccessButton';
import DeleteButton from '../delete-permission-button/DeletePermissionButton';
import PermissionStatus from '../permission-status/PermissionStatus';
import ReactivateButton from '../reactivate-permission-button/ReactivatePermissionButton';
import WithdrawButton from '../withdraw-permission-button/WithdrawPermissionButton';
import styles from './PermissionsTable.module.css';

type PermissionsTableProps = {
  productId: string;
  permissions: IPermission[];
  isActivePcfExist: boolean;
};

const PermissionsTable = ({
  productId,
  permissions,
  isActivePcfExist,
}: PermissionsTableProps): JSX.Element => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const dateApprovedTemplate = (rowData: IPermission): JSX.Element => {
    return <span>{parseDateWithoutTime(rowData.dateCreated, language as Locale)}</span>;
  };

  const approvedByTemplate = (rowData: IPermission): JSX.Element => {
    return (
      <div className='flex gap-1'>
        <span>{rowData.createdBy.firstName}</span>
        <span>{rowData.createdBy.lastName}</span>
      </div>
    );
  };

  const permissionStatusTemplate = (rowData: IPermission): JSX.Element | null => {
    if (rowData.canDeleteAccess) {
      return null;
    }

    return (
      <div className='flex justify-content-center'>
        <PermissionStatus permission={rowData} />
      </div>
    );
  };

  const actionButtonTemplate = (rowData: IPermission): JSX.Element | undefined => {
    if (rowData.canDeleteAccess) {
      return <DeleteButton permission={rowData} />;
    }

    if (rowData.status === PermissionStatusType.ACTIVE) {
      return <WithdrawButton permission={rowData} />;
    }

    if (rowData.status === PermissionStatusType.WITHDRAWN) {
      return (
        <div className='flex align-items-center'>
          <TooltipIcon
            id='reactivate-button'
            tooltipText={t('productDetailsPage.tabs.permissionsTab.reactivateTooltip')}
            icon={<InfoIconPrimary />}
          />
          <ReactivateButton permission={rowData} />
        </div>
      );
    }
  };

  if (!permissions.length) {
    if (isActivePcfExist) {
      return (
        <EmptyScreen>
          <p className='flex text-center font-light'>
            {t('productDetailsPage.tabs.permissionsTab.emptyScreenTextWithActivePcf')}
          </p>
          <AddCompanyAccessButton productId={productId} />
        </EmptyScreen>
      );
    }

    return (
      <EmptyScreen>
        <p className='flex text-center font-light'>
          {t('productDetailsPage.tabs.permissionsTab.emptyScreenTextWithoutActivePcf')}
        </p>
      </EmptyScreen>
    );
  }

  return (
    <div className={styles['permissions-table']}>
      <DataTable
        value={permissions}
        pt={{
          wrapper: {
            className: styles['table-wrapper'],
          },
        }}
        emptyMessage={t('global.emptyTableMessage')}
      >
        <Column
          field='companyName'
          sortable
          header={t('productDetailsPage.tabs.permissionsTab.permissionsTableColumns.companyName')}
          style={{ minWidth: '12rem', maxWidth: '16rem' }}
        />
        <Column
          field='bpn'
          sortable
          header={t('productDetailsPage.tabs.permissionsTab.permissionsTableColumns.bpn')}
          style={{ minWidth: '12rem', maxWidth: '16rem' }}
        />
        <Column
          header={t('productDetailsPage.tabs.permissionsTab.permissionsTableColumns.dateApproved')}
          body={dateApprovedTemplate}
          sortable
          sortFunction={genericSortFunction}
          sortField='dateCreated'
          style={{ minWidth: '12rem', maxWidth: '16rem' }}
        />
        <Column
          header={t('productDetailsPage.tabs.permissionsTab.permissionsTableColumns.approvedBy')}
          body={approvedByTemplate}
          sortable
          sortFunction={genericSortFunction}
          sortField='createdBy.firstName,createdBy.lastName'
          style={{ minWidth: '12rem', maxWidth: '16rem' }}
        />
        <Column body={permissionStatusTemplate} style={{ minWidth: '12rem', maxWidth: '16rem' }} />
        <Column body={actionButtonTemplate} style={{ minWidth: '12rem', maxWidth: '16rem' }} />
      </DataTable>
    </div>
  );
};

export default PermissionsTable;
